import { Plus } from 'lucide-react';
import { Link } from 'react-router';

import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';
import { fechaHora } from '@/lib/formato';

import { useStaffLista } from './queries';

const CLAVES = ['q', 'status'] as const;

function Lista() {
  const { filtros, cambiar } = useFiltrosUrl(CLAVES);
  const puedeEditar = useCan('staff', 'editar');
  const q = useStaffLista({
    page: filtros.page,
    ...(filtros.q && { q: filtros.q }),
    ...(filtros.status && { status: filtros.status as 'active' | 'disabled' }),
  });
  return (
    <>
      <Encabezado
        titulo="Usuarios staff"
        descripcion="Cuentas del panel y las secciones a las que tiene acceso cada una."
        acciones={
          puedeEditar && (
            <Link to="/staff/nuevo" className="btn-cs btn-primario">
              <Plus size={16} aria-hidden /> Nuevo staff
            </Link>
          )
        }
      />
      <div className="mb-4 flex flex-wrap gap-3">
        <input
          className="input-vidrio max-w-xs"
          placeholder="Buscar por nombre o correo"
          defaultValue={filtros.q}
          onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value.trim() })}
          onBlur={(e) => cambiar({ q: e.target.value.trim() })}
          aria-label="Buscar"
        />
        <select
          className="input-vidrio max-w-48"
          value={filtros.status}
          onChange={(e) => cambiar({ status: e.target.value })}
          aria-label="Estado"
        >
          <option value="">Todas</option>
          <option value="active">Activas</option>
          <option value="disabled">Desactivadas</option>
        </select>
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin cuentas con esos filtros" />}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Último acceso</th>
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/staff/${s.id}`} className="font-bold">
                      {s.name}
                    </Link>
                  </td>
                  <td>{s.email}</td>
                  <td>{s.isSuperadmin ? 'Superadmin' : 'Por secciones'}</td>
                  <td>
                    {s.status === 'active' ? (
                      s.hasPassword ? (
                        <span className="insignia insignia-activa">Activa</span>
                      ) : (
                        <span className="insignia">Invitación pendiente</span>
                      )
                    ) : (
                      <span className="insignia insignia-exclusivo">Desactivada</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap">{fechaHora(s.lastLoginAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
    </>
  );
}

export function StaffListaPage() {
  return (
    <PermissionGate seccion="staff">
      <Lista />
    </PermissionGate>
  );
}
